import { useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight, Filter, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { cn } from '@/lib/utils';
import { useApi } from '@/hooks/useApi';

export interface RouteFilterValues {
  search: string;
  from_zone_id: string;
  to_zone_id: string;
  is_active: string;
}

interface RouteFilterSidebarProps {
  filters: RouteFilterValues;
  onFilterChange: (key: keyof RouteFilterValues, value: string) => void;
  onClearFilters: () => void;
  className?: string;
}

interface ZoneOption {
  value: string;
  label: string;
}

const statusOptions: ZoneOption[] = [
  { value: 'true', label: 'نشط' },
  { value: 'false', label: 'غير نشط' },
];

export function RouteFilterSidebar({
  filters,
  onFilterChange,
  onClearFilters,
  className,
}: RouteFilterSidebarProps) {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [zoneOptions, setZoneOptions] = useState<ZoneOption[]>([]);
  const { get } = useApi();

  const fetchZones = useCallback(async () => {
    try {
      const response: any = await get('/api/v1/settings/delivery-zones');
      const data = Array.isArray(response) ? response : response?.data || response?.list || [];
      setZoneOptions(
        data.map((z: any) => ({
          value: String(z.id),
          label: `${z.code} - ${z.name}`,
        }))
      );
    } catch (error) {
      console.error('Error fetching zones', error);
    }
  }, []);

  useEffect(() => {
    fetchZones();
  }, [fetchZones]);

  const hasActiveFilters = Object.values(filters).some((v) => v !== '');

  return (
    <div
      className={cn(
        'relative transition-all duration-300 ease-in-out',
        isCollapsed ? 'w-12' : 'w-52',
        className
      )}
    >
      {isCollapsed ? (
        <div className="flex h-full flex-col items-center pt-4">
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6"
            onClick={() => setIsCollapsed(false)}
          >
            <ChevronLeft className="h-3 w-3" />
          </Button>
          <Filter className="h-5 w-5 text-muted-foreground mt-2" />
          {hasActiveFilters && (
            <div className="mt-2 h-2 w-2 rounded-full bg-primary" />
          )}
        </div>
      ) : (
        <div className="space-y-2 pe-2 h-full overflow-y-auto pb-4">
          {/* Header */}
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold flex items-center gap-2">
              <Filter className="h-4 w-4" />
              فلترة
            </h3>
            <div className="flex items-center gap-1">
              {hasActiveFilters && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 text-xs text-destructive hover:text-destructive px-2"
                  onClick={onClearFilters}
                >
                  مسح
                </Button>
              )}
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6"
                onClick={() => setIsCollapsed(true)}
              >
                <ChevronRight className="h-3 w-3" />
              </Button>
            </div>
          </div>

          {/* Search Input */}
          <div className="relative">
            <Search className="absolute end-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input
              type="text"
              placeholder="بحث..."
              value={filters.search}
              onChange={(e) => onFilterChange('search', e.target.value)}
              className="h-9 text-xs pe-8"
            />
          </div>

          {/* From Zone Filter */}
          <SearchableSelect
            options={zoneOptions}
            value={filters.from_zone_id}
            onValueChange={(v) => onFilterChange('from_zone_id', v)}
            placeholder="منطقة الانطلاق"
            searchPlaceholder="بحث في المناطق..."
          />

          {/* To Zone Filter */}
          <SearchableSelect
            options={zoneOptions}
            value={filters.to_zone_id}
            onValueChange={(v) => onFilterChange('to_zone_id', v)}
            placeholder="منطقة الوصول"
            searchPlaceholder="بحث في المناطق..."
          />

          {/* Status Filter */}
          <SearchableSelect
            options={statusOptions}
            value={filters.is_active}
            onValueChange={(v) => onFilterChange('is_active', v)}
            placeholder="الحالة"
            searchPlaceholder="بحث..."
          />
        </div>
      )}
    </div>
  );
}
